import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash, FaFileExport } from "react-icons/fa";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";


interface Zone {
  id: string;
  name: string;
  warehouseId: string;
  tenantId: string;
  createdByUserId: string;
}

const ZonesList = () => {
  const [zones, setZones] = useState<Zone[]>([]);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<keyof Zone | "">("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [warehouses, setWarehouses] = useState<{ id: string; name: string }[]>([]);


  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL;

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      },
    };
  };

  const fetchZones = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/zones`, {
        ...getAuthHeader(),
        params: {
          page: currentPage,
          limit: rowsPerPage,
          search,
          sortField,
          sortOrder: sortOrder.toUpperCase(),
        },
      });
      setZones(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error("Failed to fetch zones", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchZones();
  }, [currentPage, rowsPerPage, sortField, sortOrder]);

  useEffect(() => {
    fetchWarehouses();
  }, []);


  useEffect(() => {
    const timeout = setTimeout(() => {
      setCurrentPage(1);
      fetchZones();
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  const handleSort = (field: keyof Zone) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
  };

  const deleteZone = async (id: string) => {
    if (window.confirm("Delete this zone?")) {
      try {
        await axios.delete(`${API}/zones/${id}`, getAuthHeader());
        fetchZones();
      } catch (err) {
        alert("Failed to delete zone");
      }
    }
  };

  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(zones || []);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Zones");
    const blob = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([blob]), "zones.xlsx");
  };

  const maxPage = Math.ceil(total / rowsPerPage);

  const currentData = useMemo(() => {
    return zones || [];
  }, [zones]);

  const fetchWarehouses = async () => {
	  try {
		const res = await axios.get(`${API}/warehouses`, getAuthHeader());
		setWarehouses(res.data.data || []);
	  } catch (error) {
		console.error("Failed to fetch warehouses", error);
	  }
	};

  return (
    <div className="p-1 mx-auto max-w-6xl">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-lg font-semibold text-gray-800">Zones</h1>
          <button
            className="bg-blue-600 text-white text-[13px] px-3 py-1 rounded hover:bg-blue-700"
            onClick={() => navigate("/zone/create")}
          >
            + Add Zone
          </button>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-2">
          <div className="relative w-full md:w-1/3">
            <input
              type="text"
              placeholder="Search..."
              className="border px-2 py-1 rounded text-[13px] w-full pr-8"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                &#10005;
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <select
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
              className="border rounded px-2 py-1 text-[13px]"
            >
              {[10, 25, 50, 100].map((num) => (
                <option key={num} value={num}>
                  {num} / page
                </option>
              ))}
            </select>
            |
            <button
              onClick={handleExport}
              className="text-blue-600 hover:text-blue-800 text-lg"
              title="Export to Excel"
            >
              <FaFileExport />
            </button>
          </div>
        </div>

        <div className="overflow-auto">
          <table className="w-full text-[13px] table-auto border border-gray-300">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="border px-2 w-20 py-2">#</th>
                <th className="border px-2 py-1 cursor-pointer" onClick={() => handleSort("name")}>
				  Zone Name {sortField === "name" && (sortOrder === "asc" ? "▲" : "▼")}
				</th>
				<th className="border px-2 py-1 cursor-pointer" onClick={() => handleSort("warehouseId")}>
				  Warehouse {sortField === "warehouseId" && (sortOrder === "asc" ? "▲" : "▼")}
				</th>

                <th className="border px-2 py-1 w-10 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentData.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-4">
					No zones found.
				  </td>
                </tr>
              ) : (
                currentData.map((zone, index) => (
                  <tr key={zone.id} className="hover:bg-gray-50">
                    <td className="border px-2 py-1 text-center">
                      {(currentPage - 1) * rowsPerPage + index + 1}
                    </td>
                    <td className="border px-2 py-1">{zone.name}</td>
					<td className="border px-2 py-1">
					  {warehouses.find((w) => w.id === zone.warehouseId)?.name || "-"}
					</td>
                    <td className="border px-2 py-1 text-center">
                      <button
                        className="text-blue-600 hover:text-blue-800 mr-2"
                        title="Detail"
                        onClick={() => navigate(`/zone/edit/${zone.id}`)}
                      >
                        <FaEdit />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-4 text-[13px] flex-wrap gap-2">
          <div>
            Showing {(currentPage - 1) * rowsPerPage + 1}–
            {Math.min(currentPage * rowsPerPage, total)} of {total}
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <button
              className="px-2 py-1 border rounded disabled:opacity-30 text-gray-600 hover:bg-gray-100"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(1)}
            >
              <ChevronsLeft size={18} />
            </button>
            <button
              className="px-2 py-1 border rounded disabled:opacity-30 text-gray-600 hover:bg-gray-100"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            >
              «
            </button>

            {currentPage > 3 && (
              <>
                <button className="px-2 py-1 border rounded" onClick={() => setCurrentPage(1)}>
                  1
                </button>
                {currentPage > 4 && <span className="px-1">...</span>}
              </>
            )}

            {Array.from({ length: maxPage }, (_, i) => i + 1)
              .filter((page) => Math.abs(page - currentPage) <= 2)
              .map((page) => (
                <button
                  key={page}
                  className={`px-2 py-1 border rounded ${
                    page === currentPage
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                  }`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}

            {currentPage < maxPage - 2 && (
              <>
                {currentPage < maxPage - 3 && <span className="px-1">...</span>}
                <button
                  className="px-2 py-1 border rounded bg-white text-gray-700 hover:bg-gray-100"
                  onClick={() => setCurrentPage(maxPage)}
                >
                  {maxPage}
                </button>
              </>
            )}

            <button
              className="px-2 py-1 border rounded disabled:opacity-30 text-gray-600 hover:bg-gray-100"
              disabled={currentPage === maxPage}
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, maxPage))}
            >
              »
            </button>
            <button
              className="px-2 py-1 border rounded disabled:opacity-30 text-gray-600 hover:bg-gray-100"
              disabled={currentPage === maxPage}
              onClick={() => setCurrentPage(maxPage)}
            >
              <ChevronsRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZonesList;
